import React, { useState, useEffect } from 'react';
import './DashboardHeader.css';
import defaultProfile from '../../../assets/images/dashboard-header-profile.jpg';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../../UserContext';
import { Settings, Bell, Menu, Crown, ArrowRight, X, ExternalLink } from 'lucide-react';
import { getMembershipCapabilities } from '../../../utils/membershipCapabilities';
import { appConfig } from '../../config/appConfig';
import { adminService } from '../../../services';

const DashboardHeader = ({ toggleSidebar, opened }) => {
    const { user, loading, initialized } = useUser();
    const [greeting, setGreeting] = useState('');
    const [banners, setBanners] = useState([]);
    const [dismissedBanners, setDismissedBanners] = useState(() => {
        const saved = localStorage.getItem('dismissedBanners');
        return saved ? JSON.parse(saved) : [];
    });
    const location = useLocation();

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) {
            setGreeting('Good Morning');
        } else if (hour >= 12 && hour < 18) {
            setGreeting('Good Afternoon');
        } else {
            setGreeting('Good Evening');
        }

        // Fetch active banners
        const fetchBanners = async () => {
            try {
                const data = await adminService.getActiveBanners();
                setBanners(data || []);
            } catch (err) {
                console.error('Failed to fetch banners:', err);
            }
        };
        if (initialized && user) {
            fetchBanners();
        }
    }, [initialized, user]);

    const getSubscriptionStatus = () => {
        return getMembershipCapabilities(user?.subscription).tier;
    };

    const capabilities = getMembershipCapabilities(user?.subscription);

    const dismissBanner = (bannerId) => {
        const newDismissed = [...dismissedBanners, bannerId];
        setDismissedBanners(newDismissed);
        localStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
    };

    const isDashboardHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/home';

    const visibleBanners = isDashboardHome
        ? banners.filter((banner) => {
            if (dismissedBanners.includes(banner.bannerId)) return false;
            if (banner.showOnWeb === false) return false;
            if (capabilities.isPaid && banner.type === 'Promotion' && (banner.linkUrl || '').includes('/dashboard/subscription')) {
                return false;
            }
            return true;
        })
        : [];

    if (!initialized || loading) {
        return (
            <div className="dashboard-header">
                <div className="dashboard-header-skeleton">
                    <div className="skeleton-line"></div>
                </div>
            </div>
        );
    }
    if (!user) return <div className="error">No user data available.</div>;

    const profilePicture = user.profilePictureUrl || defaultProfile;

    return (
        <>
            {visibleBanners.length > 0 && (
                <div className="dashboard-banners">
                    {visibleBanners.map(banner => (
                        <div 
                            key={banner.bannerId}
                            className="dashboard-banner"
                            style={{ 
                                backgroundColor: banner.backgroundColor,
                                color: banner.textColor
                            }}
                        >
                            <div className="banner-content">
                                <strong>{banner.title}</strong>
                                <span>{banner.message}</span>
                                {banner.linkUrl && (
                                    <a 
                                        href={banner.linkUrl} 
                                        target={banner.linkUrl.startsWith('/dashboard') ? '_self' : '_blank'} 
                                        rel={banner.linkUrl.startsWith('/dashboard') ? undefined : 'noopener noreferrer'}
                                        style={{ color: banner.textColor }}
                                    >
                                        {banner.linkText || 'Learn More'} <ExternalLink size={14} />
                                    </a>
                                )}
                            </div>
                            {banner.isDismissible && (
                                <button 
                                    onClick={() => dismissBanner(banner.bannerId)}
                                    className="banner-dismiss"
                                    style={{ color: banner.textColor }}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <header className="dashboard-header">
            <div className="dashboard-header-left">
                <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                    <Menu size={24} />
                </button>
                <div className="header-greeting">
                    <h1>{greeting}, <span className="header-username">{user.firstName}</span></h1>
                    <div className="header-status-row">
                        <span className={`header-membership-badge ${capabilities.isPaid ? 'premium' : 'free'}`}>
                            {capabilities.isPaid && <Crown size={14} />}
                            {getSubscriptionStatus()} Member
                        </span>
                        {!capabilities.isPaid && (
                            <Link to="/dashboard/subscription" className="header-upgrade-link">
                                Compare Plans
                                <ArrowRight size={16} />
                            </Link>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="dashboard-header-right">
                <div className="header-beta-notice">
                    <span>Beta</span> - Share feedback at {appConfig.supportEmail}
                </div>
                <button className="header-icon-btn">
                    <Bell size={20} />
                </button>
                <Link to="/dashboard/setting" className="header-icon-btn">
                    <Settings size={20} />
                </Link>
                <Link to="/dashboard/setting" className="header-profile">
                    <img src={profilePicture} alt="Profile" />
                </Link>
            </div>
        </header>
        </>
    );
};

export default DashboardHeader;
